import { useState } from 'react';
import { Sun, Home, Recycle, DollarSign, AlertCircle } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { useApp } from '@/contexts/AppContext';
import { usePropertyAnalytics } from '@/hooks/usePropertyAnalytics';
import { PeriodSelector, periodToDates, type AnalyticsPeriod } from './PeriodSelector';
import { AnalyticsSummaryCard } from './AnalyticsSummaryCard';
import { AnalyticsSkeleton } from './AnalyticsSkeleton';
import { EnergyTrendChart } from './EnergyTrendChart';
import { SystemProofCard } from './SystemProofCard';
import { SystemHealthCard } from './SystemHealthCard';
import { PaybackTrackerCard } from './PaybackTrackerCard';
import { BatteryHealthCard } from './BatteryHealthCard';
import { EnvironmentalImpactCard } from './EnvironmentalImpactCard';
import { formatCurtailmentDollars } from '@/components/dashboard/CurtailmentCard';
import { MonthlyReportsList } from './MonthlyReportsList';

export function HomeownerAnalyticsView() {
  const { selectedPropertyId, properties } = useApp();
  const selectedProperty = properties.find((p) => p.id === selectedPropertyId);
  const [period, setPeriod] = useState<AnalyticsPeriod>('30d');
  const { dateFrom, dateTo } = periodToDates(period);

  const { data, isLoading, error } = usePropertyAnalytics(selectedPropertyId ?? undefined, dateFrom, dateTo);

  if (!selectedPropertyId) {
    return (
      <Card className="bg-wt-bg-secondary border-border">
        <CardContent className="py-12 flex flex-col items-center justify-center text-center">
          <AlertCircle className="h-12 w-12 text-wt-text-secondary mb-4" />
          <p className="text-wt-text-secondary">Select a property to view analytics</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold">{selectedProperty?.name || 'Property'} Analytics</h1>
          <p className="text-wt-text-secondary mt-1">
            Performance summary for {selectedProperty?.name || 'your property'}
          </p>
        </div>
        <PeriodSelector value={period} onChange={setPeriod} />
      </div>

      {isLoading && <AnalyticsSkeleton />}

      {error && (
        <Card className="bg-wt-bg-secondary border-border">
          <CardContent className="py-12 flex flex-col items-center justify-center text-center">
            <AlertCircle className="h-8 w-8 text-destructive mb-4" />
            <p className="text-sm text-foreground">Failed to load analytics</p>
          </CardContent>
        </Card>
      )}

      {data && data.day_count === 0 && !isLoading && (
        <Card className="bg-wt-bg-secondary border-border">
          <CardContent className="py-12 flex flex-col items-center justify-center text-center">
            <Sun className="h-12 w-12 text-wt-text-secondary mb-4" />
            <p className="text-wt-text-secondary">
              Analytics will appear once your system has been monitored for at least 24 hours.
            </p>
          </CardContent>
        </Card>
      )}

      {/* System Health + Payback — always shown (not gated on analytics data) */}
      {selectedPropertyId && !isLoading && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <SystemHealthCard propertyId={selectedPropertyId} />
          <PaybackTrackerCard propertyId={selectedPropertyId} />
        </div>
      )}

      {data && data.day_count > 0 && (
        <>
          {/* KPI Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <AnalyticsSummaryCard
              label="Solar Generated"
              value={`${data.total_pv_kwh.toFixed(1)} kWh`}
              icon={<Sun className="w-5 h-5" />}
              color="default"
            />
            <AnalyticsSummaryCard
              label="Home Usage"
              value={`${data.total_load_kwh.toFixed(1)} kWh`}
              icon={<Home className="w-5 h-5" />}
              color="default"
            />
            <AnalyticsSummaryCard
              label="Self-Powered"
              value={`${data.self_consumption_pct.toFixed(1)}%`}
              subtitle={`${(data.total_pv_kwh - data.total_grid_export_kwh).toFixed(1)} kWh used directly`}
              icon={<Recycle className="w-5 h-5" />}
              color="green"
            />
            <AnalyticsSummaryCard
              label="Estimated Savings"
              value={`~$${data.estimated_savings_dollars.toFixed(0)}`}
              subtitle={
                // WT-HARNESS-421: disclose when the rate is the platform default (no configured utility rate),
                // so a defaulted savings figure isn't presented as the homeowner's real economics.
                data.rate_source_resolved === 'default'
                  ? `est. at $${(data.rate_cents_per_kwh / 100).toFixed(2)}/kWh (default rate — set yours for accuracy)`
                  : `based on $${(data.rate_cents_per_kwh / 100).toFixed(2)}/kWh`
              }
              icon={<DollarSign className="w-5 h-5" />}
              color="green"
            />
          </div>

          {/* Energy Trend */}
          {data.daily_trend && data.daily_trend.length > 0 && (
            <EnergyTrendChart data={data.daily_trend} title="Daily Energy" />
          )}

          {/* System Health + Bottom Row */}
          <SystemProofCard
            uptimePct={data.uptime_pct}
            sentCount={data.sent_count}
            resolvedCount={data.resolved_count}
            criticalCount={data.critical_count}
            warningCount={data.warning_count}
          />

          {/* Curtailment value — informational, conservative framing per ADR-003 */}
          {data.total_curtailed_kwh > 0 && (
            <Card className="bg-wt-bg-secondary border-border">
              <CardContent className="py-4 px-5">
                <p className="text-sm font-medium text-foreground">
                  Your system curtailed {data.total_curtailed_kwh.toFixed(1)} kWh this month
                  {data.curtailment_value_dollars > 0 && (
                    <span className="text-muted-foreground">
                      {' '}(~{formatCurtailmentDollars(data.curtailment_value_dollars)})
                    </span>
                  )}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  A battery expansion could capture this energy
                </p>
              </CardContent>
            </Card>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {data.battery_capacity_kwh != null && data.battery_degradation_pct != null && data.battery_snapshot_at && (
              <BatteryHealthCard
                capacityKwh={data.battery_capacity_kwh}
                degradationPct={data.battery_degradation_pct}
                cycles={data.battery_cycles}
                snapshotAt={data.battery_snapshot_at}
              />
            )}
            <EnvironmentalImpactCard
              co2AvoidedKg={data.co2_avoided_kg}
              treesEquivalent={data.trees_equivalent}
              selfConsumptionPct={data.self_consumption_pct}
            />
          </div>
        </>
      )}

      {/* Monthly Reports — always shown if property selected */}
      {selectedPropertyId && !isLoading && (
        <MonthlyReportsList propertyId={selectedPropertyId} />
      )}
    </div>
  );
}
